import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormHookField } from '@/components/ui/form-hook-field';
import { StickyPageHeader } from '@/components/ui/sticky-page-header';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  MapPin, 
  Save,
  X
} from 'lucide-react';
import { LocationSearch } from '@/features-flat/map/LocationSearch';
import { useGuests } from './useGuests';
import { useFeedback } from "@/shared/contexts/FeedbackContext";
import { 
  guestInputSchema, 
  locationSchema, 
  GuestStatus,
  Guest,
  Location
} from '@/types/models/schema';
import { ApiResponse } from '@/types/api';

/**
 * Extended location schema for form display with address fields
 */
const formLocationSchema = locationSchema.extend({
  address: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
});

type FormLocation = z.infer<typeof formLocationSchema>;

/**
 * Social media schema for form
 */
const socialMediaSchema = z.object({
  linkedin: z.string().optional(),
  twitter: z.string().optional(),
  website: z.string().optional(),
});

type SocialMedia = z.infer<typeof socialMediaSchema>;

/**
 * Form schema based on the shared guestInputSchema with form-specific extensions
 */
const formSchema = guestInputSchema.extend({
  // Override location to include address field for form display
  location: formLocationSchema,
  // Add form-specific fields not in the database schema
  episode_number: z.number().optional().nullable(),
  episode_date: z.string().optional().nullable(),
  social_media: socialMediaSchema.optional().nullable(),
});

// Type for the form values
type GuestFormValues = z.infer<typeof formSchema>;

// Default empty location object
const defaultLocation: FormLocation = {
  lat: 0,
  lng: 0,
  name: '',
  address: '',
  city: undefined,
  country: undefined
};

/**
 * Props interface for GuestForm component
 */
interface GuestFormProps {
  /** Optional initial guest data for editing mode */
  initialGuest?: Guest;
}

/**
 * GuestForm Component
 * 
 * Form for creating and editing podcast guests with sticky header and action buttons
 * 
 * @param props - Component props
 * @returns React component
 */
const GuestForm: React.FC<GuestFormProps> = ({ initialGuest }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast: showToast } = useFeedback();
  const { getGuest, createGuest, updateGuest } = useGuests();
  const [loading, setLoading] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(id ? true : false);
  
  // Component initialization
  React.useEffect(() => {
    // Component mounted - sticky header should be properly positioned
  }, []);
  
  // Form setup
  const form = useForm<GuestFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      title: '',
      bio: '',
      status: 'pending' as GuestStatus,
      location: defaultLocation,
      social_media: {
        linkedin: '',
        twitter: '',
        website: '',
      },
      team_members: [],
      clients: [],
      episode_number: null,
      episode_date: '',
    },
  });
  
  // Form setup complete
  
  // Load guest data if editing
  React.useEffect(() => {
    const loadGuest = async () => {
      if (!id) {
        console.log('✅ GuestForm: No ID found, creating new guest');
        return;
      }
      
      console.log('🔄 GuestForm: Loading guest data for ID:', id);
      
      try {
        setInitialLoading(true);
        console.log('📡 GuestForm: Calling getGuest API...');
        const guestResponse = await getGuest(id);
        
        console.log('📨 GuestForm: getGuest response:', guestResponse);
        
        if (!guestResponse || !guestResponse.data) {
          console.error('❌ GuestForm: No guest data found');
          throw new Error('Guest not found');
        }
        
        const guest = guestResponse.data;
        console.log('✅ GuestForm: Guest data loaded:', guest);
        
        // Populate form with guest data
        const formData = {
          name: guest.name,
          email: guest.email,
          company: guest.company || '',
          title: guest.title || '',
          bio: guest.bio || '',
          status: guest.status,
          location: guest.location ? {
            ...guest.location,
            // Add address field for form display
            address: guest.location.name || '',
            city: guest.location.city,
            country: guest.location.country,
          } : defaultLocation,
          team_members: guest.team_members || [],
          clients: guest.clients || [],
          social_media: guest.social_media || {
            linkedin: '',
            twitter: '',
            website: '',
          },
          episode_number: guest.episode_number || null,
          episode_date: guest.episode_date || '',
        };
        
        console.log('📝 GuestForm: Setting form data:', formData);
        form.reset(formData);
      } catch (error) {
        console.error('❌ GuestForm: Error loading guest:', error);
        showToast({
          title: 'Error',
          description: 'Failed to load guest data',
          type: 'destructive',
        });
        navigate('/guests');
      } finally {
        setInitialLoading(false);
        console.log('🔄 GuestForm: Initial loading complete');
      }
    };
    
    loadGuest();
  }, [id, getGuest, form, navigate, showToast]);
  
  // Transform form values to GuestInput format
  const transformFormToGuestInput = (values: GuestFormValues): Omit<Guest, 'id' | 'created_at' | 'updated_at'> => {
    console.log('🔄 GuestForm: Transforming form values:', values);
    
    const { 
      name, email, company, title, bio, status, 
      location, team_members, clients, social_media,
      episode_number, episode_date
    } = values;
    
    // Transform location to match the expected format
    const transformedLocation: Location = {
      lat: location.lat,
      lng: location.lng,
      name: location.address || location.name || '', // Use address if available, otherwise name
      city: location.city,
      country: location.country,
    };
    
    const transformedData = {
      name,
      email,
      company: company || null,
      title: title || null,
      bio: bio || null,
      status,
      location: transformedLocation,
      team_members: team_members || [],
      clients: clients || [],
      social_media: social_media || null,
      episode_number: episode_number || null,
      episode_date: episode_date || null,
    };
    
    console.log('✅ GuestForm: Transformed data:', transformedData);
    return transformedData;
  };
  
  // Handle form submission
  const onSubmit = async (values: GuestFormValues) => {
    console.log('🚀 GuestForm: Form submission started', values);
    
    try {
      setLoading(true);
      console.log('⏳ GuestForm: Setting loading state to true');
      
      const guestInput = transformFormToGuestInput(values);
      console.log('📤 GuestForm: Guest input prepared:', guestInput);
      
      let response: ApiResponse<Guest>;
      
      if (id) {
        console.log('📝 GuestForm: Updating existing guest with ID:', id);
        response = await updateGuest(id, guestInput);
      } else {
        console.log('➕ GuestForm: Creating new guest');
        response = await createGuest(guestInput);
      }
      
      console.log('📨 GuestForm: API response:', response);
      
      if (response.error) {
        console.error('❌ GuestForm: API returned error:', response.error);
        throw new Error(response.error.message || 'Failed to save guest');
      }
      
      console.log('✅ GuestForm: Guest saved successfully');
      
      showToast({
        title: id ? 'Guest Updated' : 'Guest Created',
        description: `Guest ${values.name} has been successfully ${id ? 'updated' : 'created'}.`,
        type: 'default',
      });
      
      const redirectPath = id ? `/guests/${id}` : '/guests';
      console.log('🔄 GuestForm: Redirecting to:', redirectPath);
      navigate(redirectPath);
    } catch (error) {
      console.error('❌ GuestForm: Error during submission:', error);
      showToast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
        type: 'destructive',
      });
    } finally {
      setLoading(false);
      console.log('✅ GuestForm: Loading state set to false');
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    console.log('🔙 GuestForm: Cancel button clicked');
    navigate('/guests');
  };
  
  // Handle location selection
  const handleLocationSelect = (location: FormLocation) => {
    console.log('📍 GuestForm: Location selected:', location);
    form.setValue('location', location, { shouldValidate: true });
  };
  
  if (initialLoading) {
    console.log('⏳ GuestForm: Showing loading state');
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  console.log('🎨 GuestForm: Rendering form component');
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header using reusable component */}
      <StickyPageHeader
        title={id ? 'Edit Guest' : 'Add New Guest'}
        backTo="/guests"
        backLabel="Back to Guests"
        actions={
          <>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleCancel}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <X size={16} />
              Cancel
            </Button>
            <Button 
              onClick={form.handleSubmit(onSubmit)}
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-b-transparent"></div>
                  <span>{id ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>{id ? 'Update Guest' : 'Save Guest'}</span>
                </>
              )}
            </Button>
          </>
        }
      />
      
      {/* Form Content */}
      <div className="w-full px-4 md:px-8 py-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormHookField
                    control={form.control}
                    name="name"
                    label="Name"
                    render={({ field }) => (
                      <Input placeholder="John Doe" {...field} />
                    )}
                  />
                  
                  <FormHookField
                    control={form.control}
                    name="email"
                    label="Email"
                    render={({ field }) => (
                      <Input placeholder="john@example.com" type="email" {...field} />
                    )}
                  />
                  
                  <FormHookField
                    control={form.control}
                    name="company"
                    label="Company"
                    render={({ field }) => (
                      <Input placeholder="Acme Inc." {...field} />
                    )}
                  />
                  
                  <FormHookField
                    control={form.control}
                    name="title"
                    label="Title"
                    render={({ field }) => (
                      <Input placeholder="CEO" {...field} />
                    )}
                  />
                </div>
                
                <FormHookField
                  control={form.control}
                  name="bio"
                  label="Bio"
                  render={({ field }) => (
                    <Textarea 
                      placeholder="Brief description of the guest..." 
                      className="min-h-[120px]" 
                      {...field} 
                    />
                  )}
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Status & Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <FormHookField
                     control={form.control}
                     name="status"
                     label="Status"
                     render={({ field }) => (
                       <Select onValueChange={field.onChange} defaultValue={field.value}>
                         <SelectTrigger>
                           <SelectValue placeholder="Select status" />
                         </SelectTrigger>
                         <SelectContent>
                           <SelectItem value="pending">Pending</SelectItem>
                           <SelectItem value="contacted">Contacted</SelectItem>
                           <SelectItem value="scheduled">Scheduled</SelectItem>
                           <SelectItem value="confirmed">Confirmed</SelectItem>
                           <SelectItem value="recorded">Recorded</SelectItem>
                           <SelectItem value="published">Published</SelectItem>
                           <SelectItem value="declined">Declined</SelectItem>
                           <SelectItem value="cancelled">Cancelled</SelectItem>
                         </SelectContent>
                       </Select>
                     )}
                   />
                   
                   <FormHookField
                     control={form.control}
                     name="episode_number"
                     label="Episode Number"
                     render={({ field }) => (
                       <Input 
                         type="number" 
                         placeholder="123" 
                         {...field} 
                         value={field.value || ''}
                         onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                       />
                     )}
                   />
                 </div>
                
                <FormHookField
                  control={form.control}
                  name="episode_date"
                  label="Episode Date"
                  render={({ field }) => (
                    <Input 
                      type="date" 
                      {...field} 
                      value={field.value || ''}
                    />
                  )}
                />
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Location</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Search Location</Label>
                    <LocationSearch onSelect={handleLocationSelect} />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormHookField
                      control={form.control}
                      name="location.address"
                      label="Address"
                      render={({ field }) => (
                        <Input {...field} />
                      )}
                    />
                    
                    <FormHookField
                      control={form.control}
                      name="location.city"
                      label="City"
                      render={({ field }) => (
                        <Input {...field} value={field.value || ''} />
                      )}
                    />
                    
                    <FormHookField
                      control={form.control}
                      name="location.country"
                      label="Country"
                      render={({ field }) => (
                        <Input {...field} value={field.value || ''} />
                      )}
                    />
                  </div>
                  
                  {form.watch('location') && form.watch('location.address') && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-neutral-50 rounded border">
                      <MapPin size={16} className="text-neutral-400" />
                      <span className="text-sm">{form.watch('location.address')}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Social Media</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormHookField
                    control={form.control}
                    name="social_media.linkedin"
                    label="LinkedIn"
                    render={({ field }) => (
                      <Input placeholder="https://linkedin.com/in/..." {...field} />
                    )}
                  />
                  
                  <FormHookField
                    control={form.control}
                    name="social_media.twitter"
                    label="Twitter"
                    render={({ field }) => (
                      <Input placeholder="https://twitter.com/..." {...field} />
                    )}
                  />
                  
                  <FormHookField
                    control={form.control}
                    name="social_media.website"
                    label="Website"
                    render={({ field }) => (
                      <Input placeholder="https://example.com" {...field} />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Bottom spacer for sticky header */}
            <div className="pb-20" />
          </form>
        </Form>
      </div>
    </div>
  );
};

export default GuestForm;
